import { startCase } from "lodash";
import { ValidationError, ValidationErrorSource } from "@/shared/interfaces/validation-error.interface";

export interface GroupedErrors {
  source: ValidationErrorSource;
  displayName: string;
  errors: ValidationError[];
}

export function getSourceDisplayName(source: ValidationErrorSource, fileName?: string): string {
  switch (source) {
    case "form":
      return "Form Fields";
    case "formula":
      return "Composite Formulas";
    case "reference":
      return "Reference Validation";
    case "file":
      return fileName ?? "CSV Files";
    default:
      return "Other";
  }
}

export function getSourceOrder(source: ValidationErrorSource): number {
  switch (source) {
    case "form":
      return 0;
    case "formula":
      return 1;
    case "reference":
      return 2;
    case "file":
      return 3;
    default:
      return 4;
  }
}

export function groupErrorsBySource(errors: ValidationError[]): GroupedErrors[] {
  const groups = new Map<string, GroupedErrors>();

  for (const error of errors) {
    const fileName = error.source === "file" ? error.file : undefined;
    const groupKey = error.source === "file" && fileName ? `file:${fileName}` : error.source;
    const displayName = getSourceDisplayName(error.source, fileName);

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { source: error.source, displayName, errors: [] });
    }

    const group = groups.get(groupKey);
    if (group !== undefined) {
      group.errors.push(error);
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    const orderDiff = getSourceOrder(a.source) - getSourceOrder(b.source);
    if (orderDiff !== 0) return orderDiff;
    return a.displayName.localeCompare(b.displayName);
  });
}

export function getErrorLocation(error: ValidationError): string {
  const parts: string[] = [];

  if (error.line != null) {
    parts.push(`Line ${error.line}`);
  }

  if (error.row != null) {
    parts.push(`Row ${error.row}`);
  }

  if (error.column != null) {
    parts.push(`Column "${error.column}"`);
  }

  // Field names from form errors are camelCase keys (e.g. "testName"); start-case them for readability.
  // Ignore fields from other sources. They include the field in the message already.
  if (error.field && error.source === "form") {
    parts.push(startCase(error.field));
  }

  return parts.join(", ");
}
